import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../../utils/bookingApi';
import { formatDayDate } from '../../../utils/dates';
import { PageHeader, Skeleton, EmptyState, ErrorState } from '../../../components/ui';
import { CONCERN_TYPE_LABEL, SEVERITY_LABEL, SEVERITY_TONE } from '../../../components/welfare/welfareLabels';
import '../booking-shared.css';
import './Safeguarding.css';

export const WELFARE_PATH = '/booking/admin/welfare';

export function SeverityPill({ severity }) {
  return (
    <span className={`bk-pill bk-pill--${SEVERITY_TONE[severity] || 'off'}`}>
      {SEVERITY_LABEL[severity] ?? severity}
    </span>
  );
}

const ROLE_LABEL = {
  CLUB_ADMIN: 'Admin', COACH: 'Coach', WELFARE: 'Welfare officer', ADULT: 'Adult member',
};

// Who the report is about: a gymnast, or an adult such as a coach.
export function subjectName(report) {
  if (report.gymnast) return `${report.gymnast.firstName} ${report.gymnast.lastName}`;
  if (report.subjectUser) {
    const role = ROLE_LABEL[report.subjectUser.role];
    return `${report.subjectUser.firstName} ${report.subjectUser.lastName}${role ? ` (${role})` : ''}`;
  }
  return 'No one named';
}

const excerpt = (text) => (text.length > 120 ? `${text.slice(0, 120)}…` : text);

export default function AdminWelfare() {
  const [reports, setReports] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setError(false);
    bookingApi.getWelfareReports()
      .then(res => setReports(res.data))
      .catch((err) => { console.error('Could not load welfare reports', err); setError(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const header = (
    <PageHeader
      back={{ to: '/admin-hub', label: 'Admin' }}
      title="Welfare reports"
      details="Confidential. Only club admins and welfare officers can see them."
      action={{ label: 'New report', short: 'New', to: `${WELFARE_PATH}/new` }}
    />
  );

  if (error) {
    return (
      <div className="bk-page bk-page--wide">
        {header}
        <ErrorState title="Couldn't load welfare reports" onRetry={load} />
      </div>
    );
  }

  return (
    <div className="bk-page bk-page--wide">
      {header}
      {!reports && <Skeleton rows={4} label="Loading welfare reports…" />}
      {reports && reports.length === 0 && (
        <EmptyState
          icon={ShieldExclamationIcon}
          title="No welfare reports yet"
          text="Record any welfare or safeguarding concern here, so there's a signed record of what happened and what was done."
          action={<Link className="bk-btn bk-btn--primary" to={`${WELFARE_PATH}/new`}>New report</Link>}
        />
      )}
      {reports && reports.length > 0 && (
        <ul className="sg-list">
          {reports.map(r => (
            <li key={r.id}>
              <Link to={`${WELFARE_PATH}/${r.id}`} className="bk-card sg-item">
                <div className="sg-item__top">
                  <div>
                    <span className="sg-item__name">{subjectName(r)}</span>
                    <span className="bk-small"> {formatDayDate(r.incidentDate)}</span>
                  </div>
                  <div className="sg-item__tags">
                    <SeverityPill severity={r.severity} />
                    <span className="bk-small">{CONCERN_TYPE_LABEL[r.concernType] ?? r.concernType}</span>
                    {r.referredExternally && <span className="bk-pill bk-pill--info">Referred</span>}
                  </div>
                </div>
                <p className="sg-item__text">{excerpt(r.description)}</p>
                <p className="sg-item__meta">Filed by {r.reportedBy.firstName} {r.reportedBy.lastName}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
